// apps/web/src/routes/NotificationSettings.tsx
//
// /settings/notifications — per-channel pref toggles. The
// `security_alerts` topic is locked on (the backend refuses to
// disable it; this UI just disables the checkbox client-side and
// shows a tooltip explaining why).

import { useCallback, useEffect, useState } from 'react';
import { SkeletonBlock } from '../components/ui/Skeleton.js';
import { Alert } from '../components/ui/Alert.js';
import { apiGet, apiPatch, ApiError } from '../lib/apiClient.js';
import type { NotifPref } from '../lib/api.js';

const ALL_TOPICS = [
  { topic: 'security_alerts', label: 'Security alerts', locked: true,
    description: 'New sign-ins, contact changes, password / handle / account actions. Always on.' },
  { topic: 'product_updates', label: 'Product updates', locked: false,
    description: 'New features, scheduled maintenance, release notes.' },
  { topic: 'marketing', label: 'Marketing', locked: false,
    description: 'Occasional newsletters; you can unsubscribe any time.' },
];

const CHANNELS: Array<NotifPref['channel']> = ['email', 'sms', 'push'];

export function NotificationSettings(): JSX.Element {
  const [prefs, setPrefs] = useState<NotifPref[] | null>(null);
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  const [savedAt, setSavedAt] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const res = await apiGet<{ prefs: NotifPref[] }>('/api/v1/settings/notifications-prefs');
      setPrefs(res.data.prefs);
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not load preferences.',
        requestId: apiErr?.requestId ?? null,
      });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function getPref(channel: NotifPref['channel'], topic: string, fallback: boolean): boolean {
    const row = prefs?.find((p) => p.channel === channel && p.topic === topic);
    return row ? row.enabled : fallback;
  }

  async function setPref(channel: NotifPref['channel'], topic: string, enabled: boolean): Promise<void> {
    if (topic === 'security_alerts' && !enabled) return; // locked
    // Optimistic: update local state.
    setPrefs((prev) => {
      const list = prev ?? [];
      const idx = list.findIndex((p) => p.channel === channel && p.topic === topic);
      if (idx === -1) return [...list, { channel, topic, enabled }];
      const copy = list.slice();
      copy[idx] = { channel, topic, enabled };
      return copy;
    });
    setSaving(true);
    setError(null);
    try {
      await apiPatch<{ ok: boolean }>('/api/v1/settings/notifications-prefs', {
        prefs: [{ channel, topic, enabled }],
      });
      setSavedAt(Date.now());
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not save preferences.',
        requestId: apiErr?.requestId ?? null,
      });
      // Roll back by reloading.
      void load();
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-bold">Notifications</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Choose which notifications we send to which channel.
      </p>

      {error ? <div className="mt-4"><Alert variant="error" requestId={error.requestId}>{error.message}</Alert></div> : null}
      {savedAt > 0 ? <div className="mt-4"><Alert variant="success">Saved.</Alert></div> : null}

      {prefs === null ? (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <SkeletonBlock className="h-4 w-40" />
              <SkeletonBlock className="mt-2 h-3 w-64" />
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {ALL_TOPICS.map((t) => (
            <div key={t.topic} className="rounded border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.label}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t.description}</div>
                </div>
                {t.locked ? (
                  <span className="text-xs text-gray-500 dark:text-gray-400" title="Security alerts are always on. They protect your account.">
                    Always on
                  </span>
                ) : null}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {CHANNELS.map((c) => {
                  const enabled = getPref(c, t.topic, t.locked);
                  return (
                    <label
                      key={c}
                      className={[
                        'flex items-center justify-between gap-2 rounded border px-3 py-2 text-sm',
                        enabled
                          ? 'border-brand-500 bg-brand-50 text-brand-800 dark:border-brand-400 dark:bg-brand-950 dark:text-brand-200'
                          : 'border-gray-300 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300',
                        t.locked ? 'opacity-60' : 'cursor-pointer',
                      ].join(' ')}
                    >
                      <span className="capitalize">{c}</span>
                      <input
                        type="checkbox"
                        checked={enabled}
                        disabled={t.locked || saving}
                        onChange={(e) => void setPref(c, t.topic, e.target.checked)}
                        aria-label={`${t.label} via ${c}`}
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
